"use client";

import PolicyDocument from "./DocumentSection";

interface MarkdownDocumentProps {
  content: string;
  title: string;
  subtitle: string;
}

// Parse markdown into structured sections
function parseMarkdownToSections(content: string): {
  mainTitle: string;
  sections: Array<{
    title: string | null;
    content: string;
    isList: boolean;
    items: string[];
  }>;
} {
  if (!content.trim()) {
    return { mainTitle: "", sections: [] };
  }

  const lines = content.split("\n");
  const sections: Array<{
    title: string | null;
    content: string;
    isList: boolean;
    items: string[];
  }> = [];

  let currentTitle: string | null = null;
  let currentContent: string[] = [];
  let currentItems: string[] = [];
  let inList = false;
  let mainTitle = "";

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Skip empty lines at the start
    if (!trimmed && sections.length === 0 && currentContent.length === 0) {
      continue;
    }

    // Main title (H1)
    if (trimmed.startsWith("# ") && !mainTitle) {
      mainTitle = trimmed.replace("# ", "").trim();
      continue;
    }

    // Section title (H2 or H3)
    if (trimmed.startsWith("## ") || trimmed.startsWith("### ")) {
      // Save previous section
      if (currentTitle !== null || currentContent.length > 0 || currentItems.length > 0) {
        sections.push({
          title: currentTitle,
          content: currentContent.join("\n").trim(),
          isList: inList,
          items: [...currentItems],
        });
      }
      currentTitle = trimmed.replace(/## |### /, "").trim();
      currentContent = [];
      currentItems = [];
      inList = false;
      continue;
    }

    // List items
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      inList = true;
      const itemText = trimmed.replace(/[-*] /, "").trim();
      // Parse inline formatting
      currentItems.push(parseInlineFormatting(itemText));
      continue;
    }

    // Numbered lists
    if (/^\d+\.\s/.test(trimmed)) {
      inList = true;
      const itemText = trimmed.replace(/^\d+\.\s/, "").trim();
      currentItems.push(parseInlineFormatting(itemText));
      continue;
    }

    // Regular content
    if (trimmed) {
      if (inList && currentItems.length > 0) {
        // We were in a list, save it and start new content
        inList = false;
      }
      currentContent.push(parseInlineFormatting(trimmed));
    }
  }

  // Save last section
  if (currentTitle !== null || currentContent.length > 0 || currentItems.length > 0) {
    sections.push({
      title: currentTitle,
      content: currentContent.join("\n").trim(),
      isList: inList,
      items: [...currentItems],
    });
  }

  return { mainTitle, sections };
}

// Parse inline formatting: **bold**, *italic*, [links](url)
function parseInlineFormatting(text: string): string {
  return (
    text
      // Bold and italic
      .replace(/\*\*\*(.*?)\*\*\*/g, "***$1***")
      .replace(/\*\*(.*?)\*\*/g, "**$1**")
      .replace(/\*(.*?)\*/g, "*$1*")
      // Links - keep as is for now, will render as HTML
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-green-main hover:underline">$1</a>')
  );
}

// Render text with inline formatting
function renderFormattedText(text: string): React.ReactNode {
  if (!text) return null;

  // Split by formatting markers
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  // Process HTML links first
  const linkRegex = /<a href="([^"]+)"[^>]*>([^<]*)<\/a>/g;
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before link
    if (match.index > lastIndex) {
      parts.push(
        <span key={key++}>{renderBoldItalic(text.slice(lastIndex, match.index))}</span>
      );
    }
    // Add link
    parts.push(
      <a
        key={key++}
        href={match[1]}
        className="text-green-main hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {match[2]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{renderBoldItalic(text.slice(lastIndex))}</span>);
  }

  return parts.length > 0 ? parts : renderBoldItalic(text);
}

// Render bold and italic text
function renderBoldItalic(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  const regex = /(\*\*\*.*?\*\*\*|\*\*.*?\*\*|\*.*?\*)/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
    }

    const content = match[0];
    if (content.startsWith("***") && content.endsWith("***")) {
      parts.push(
        <strong key={key++} className="font-semibold">
          <em>{content.slice(3, -3)}</em>
        </strong>
      );
    } else if (content.startsWith("**") && content.endsWith("**")) {
      parts.push(<strong key={key++} className="font-semibold">{content.slice(2, -2)}</strong>);
    } else if (content.startsWith("*") && content.endsWith("*")) {
      parts.push(<em key={key++} className="italic">{content.slice(1, -1)}</em>);
    }

    lastIndex = match.index + content.length;
  }

  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
  }

  return parts.length > 0 ? <>{parts}</> : text;
}

export function MarkdownDocument({ content, title, subtitle }: MarkdownDocumentProps) {
  const { sections } = parseMarkdownToSections(content);

  return (
    <PolicyDocument>
      <PolicyDocument.Link title="Ir al inicio" href="/" />
      <PolicyDocument.Header title={title} subtitle={subtitle} />

      {sections.map((section, index) => (
        <PolicyDocument.Section key={index}>
          {section.title && <PolicyDocument.Title>{section.title}</PolicyDocument.Title>}
          
          {section.items.length > 0 ? (
            <PolicyDocument.Content>
              {section.content && (
                <p className="mb-3">{renderFormattedText(section.content)}</p>
              )}
              <ul className="list-disc ml-5 space-y-1">
                {section.items.map((item, itemIndex) => (
                  <li key={itemIndex}>{renderFormattedText(item)}</li>
                ))}
              </ul>
            </PolicyDocument.Content>
          ) : (
            section.content && (
              <PolicyDocument.Content>
                {section.content.split("\n").map((paragraph, pIndex) => (
                  <p key={pIndex} className={pIndex > 0 ? "mt-3" : ""}>
                    {renderFormattedText(paragraph)}
                  </p>
                ))}
              </PolicyDocument.Content>
            )
          )}
        </PolicyDocument.Section>
      ))}
    </PolicyDocument>
  );
}

export default MarkdownDocument;
